import { useCallback, useEffect, useState } from 'react';
import { useLockedScroll } from '../ui/useLockedScroll';
import { ApiError } from '../api/client';
import {
  addApplicability,
  loadApplicability,
  loadFitState,
  setEngineMatters,
  loadDonor,
  loadHistory,
  loadPhotos,
  movePart,
  removeApplicability,
  writeOffPart,
  type Applicability,
  type CatalogRow,
  type FitState,
  type PartDonor,
  type PartHistory,
  type PartPhoto,
  type Warehouse,
} from '../inventory/catalog';
import { cardFields, fitLabel } from '../inventory/partCard';
import { deletePhoto, makeMainPhoto, photoArchiveUrl, uploadPhoto } from '../inventory/photos';
import { PartCellBlock } from './PartCellBlock';
import { PartQualityBlock } from './PartQualityBlock';
import { PartEditForm } from './PartEditForm';
import { PartLabelPrint } from './PartLabelPrint';
import { LABEL_ROLES } from './tabs';
import { PartDonorView } from './PartDonorView';
import { PartHistoryView } from './PartHistoryView';
import { loadCached, modelsOf, type VehicleCatalog } from '../catalog/vehicles';
import { listCells, type Cell } from '../organization/warehouses';
import { useMounted } from '../ui/useMounted';

/**
 * Карточка позиции — как в кабинете, на который клиент смотрит каждый день:
 * узкий столбец сведений слева, крупный снимок справа.
 *
 * <p>Порядок полей взят оттуда же, и он не случайный: сначала то, что нужно
 * продавцу по телефону — где лежит, сколько стоит, какой номер, — и только
 * потом машина и номера производителя. Алфавитный порядок или порядок колонок
 * таблицы заставил бы искать цену глазами.
 *
 * <p>Данные берутся из уже загруженной строки: в ней все двадцать с лишним
 * колонок, включая скрытые в таблице. Второй запрос делается только
 * за снимками — их ссылки подписанные и короткоживущие.
 */
/** Дата без времени: в карточке время правки — шум. */

export function PartCard({ row, warehouses, role, extraFields, applicability = true,
                          quality = true, onClose, onChanged, onDonorParts }: {
  row: CatalogRow;
  warehouses: Warehouse[];
  role: string;
  /**
   * Поля, которых нет у запчасти: свойства колеса — размер, сезон, износ,
   * вылет, сверловка. Карточка одна на оба вида товара, потому что действия
   * над ними общие: цена, списание, перемещение написаны на складе,
   * а не на виде товара. Свой экран для колеса разошёлся бы с этим на первой
   * же правке.
   */
  extraFields?: Array<[string, string]>;

  /**
   * Показывать ли применимость. У колеса её нет и быть не должно: шина
   * подходит по размеру, а не по модели машины, и вкладка «Применимость»
   * с вечным «не задана» на карточке шины — это отметка о недоделанной
   * работе, которой никто никогда не сделает.
   */
  applicability?: boolean;

  /**
   * Показывать ли оценку состояния. У колеса её нет: прайс площадки,
   * из которого колёса и приезжают, шинной оценки не несёт, и строка
   * вкладки её не читает (`rowOfWheel` ставит `null` всегда). Плашка
   * «не оценена» с кнопкой на такой карточке предлагала бы записать
   * значение, которого потом нигде не видно.
   */
  quality?: boolean;
  onClose: () => void;
  /** Склад изменился: списали или перевезли — витрину надо перечитать. */
  onChanged: () => void;

  /**
   * Показать остальные детали с той же машины: экран ставит отбор по номеру
   * донора и закрывает карточку. Без обработчика кнопка не появляется —
   * на вкладке колёс отбирать по донору нечем.
   */
  onDonorParts?: (donorCode: string) => void;
}) {
  // Списывает тот, кто отвечает за деньги: списанная деталь — это убыток,
  // а не запись в журнале. Кладовщик находит недостачу, решение не его.
  const [writingOff, setWritingOff] = useState(false);
  const [reason, setReason] = useState('');
  const [writeOffAt, setWriteOffAt] = useState<number | null>(null);
  const [writeOffQty, setWriteOffQty] = useState('1');
  const [writeOffError, setWriteOffError] = useState('');

  // Перевозит кладовщик наравне с владельцем: деталь у него в руках,
  // и решение «переставить на другой склад» — это работа, а не расход.
  const [moving, setMoving] = useState(false);
  const [moveFrom, setMoveFrom] = useState<number | null>(null);
  const [moveTo, setMoveTo] = useState<number | null>(null);
  const [moveQty, setMoveQty] = useState('1');
  const [moveCells, setMoveCells] = useState<Cell[]>([]);
  const [moveCell, setMoveCell] = useState<number | null>(null);
  const [moveError, setMoveError] = useState('');
  const [photos, setPhotos] = useState<PartPhoto[]>([]);
  // Снимки правит тот, кто отвечает за то, как товар выглядит на площадке.
  // Кладовщику это не нужно: он деталь не продаёт.
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [shown, setShown] = useState(0);
  const [tab, setTab] = useState<'about' | 'fits' | 'history' | 'donor'>('about');
  const [donor, setDonor] = useState<PartDonor | null>(null);
  // История грузится по открытию вкладки, а не вместе с карточкой: у позиции
  // переехавшего клиента правок бывают десятки, а открывают карточку чаще
  // всего чтобы посмотреть цену и снимок.
  const [history, setHistory] = useState<PartHistory | null>(null);
  const [historyTab, setHistoryTab] = useState<'changes' | 'movements'>('changes');
  // Правит владелец и менеджер: здесь цена, минимальная цена
  // и себестоимость — продавец, торгующийся с покупателем, не должен уметь
  // подвинуть себе нижнюю границу.
  const [editing, setEditing] = useState(false);
  /**
   * Оценка, какой её видит карточка сейчас. Своя копия нужна ровно затем,
   * чтобы плашка показала выбранное, не закрывая окно: остальные действия
   * карточки меняют остаток и закрывают её, а тут поменялось одно поле,
   * и исчезнувшее окно вместо плашки читалось бы как «не сохранилось».
   */
  const [grade, setGrade] = useState<string | null>(row.qualityGrade);
  /** Оценку трогали — витрину за карточкой надо перечитать при закрытии. */
  const [regraded, setRegraded] = useState(false);
  const [fits, setFits] = useState<Applicability[] | null>(null);
  // «Двигатель важен» и пояснение, почему применимость не поставилась сама (0363).
  const [fitState, setFitState] = useState<FitState | null>(null);
  // Справочник машин для добавления: он предзагружен и лежит в IndexedDB,
  // тянуть четыре с половиной тысячи моделей ради одной правки незачем.
  const [vehicles, setVehicles] = useState<VehicleCatalog | null>(null);
  const [brandId, setBrandId] = useState<number | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  useEffect(() => {
    void loadPhotos(row.id)
      .then((found) => { if (mounted.current) setPhotos(found); })
      .catch(() => { if (mounted.current) setPhotos([]); });
    // Применимость нужна и на вкладке «Описание»: там стоит отметка,
    // задана она или нет, и отметка «сейчас узнаю» была бы бесполезной.
    void loadApplicability(row.id)
      .then((found) => { if (mounted.current) setFits(found); })
      .catch(() => { if (mounted.current) setFits([]); });
    void loadFitState(row.id)
      .then((found) => { if (mounted.current) setFitState(found); })
      .catch(() => { if (mounted.current) setFitState(null); });
    void loadCached()
      .then((found) => { if (mounted.current) setVehicles(found); })
      .catch(() => { if (mounted.current) setVehicles(null); });
  }, [row.id, mounted]);

  useEffect(() => {
    if (tab !== 'history' || history !== null) {
      return;
    }
    void loadHistory(row.id)
      .then((found) => { if (mounted.current) setHistory(found); })
      .catch(() => {
        if (mounted.current) setHistory({ changes: [], movements: [] });
      });
  }, [tab, history, row.id, mounted]);

  // Машина грузится по открытию вкладки: чаще всего карточку открывают ради
  // цены и снимка, а не ради того, какой у донора привод.
  useEffect(() => {
    if (tab !== 'donor' || donor !== null) {
      return;
    }
    void loadDonor(row.id)
      .then((found) => { if (mounted.current) setDonor(found); })
      .catch(() => { if (mounted.current) setDonor(null); });
  }, [tab, donor, row.id, mounted]);

  // Карточку открывают подряд по нескольким позициям: оставшаяся оценка
  // соседней означала бы плашку с чужим значением.
  useEffect(() => {
    setGrade(row.qualityGrade);
    setRegraded(false);
  }, [row.id, row.qualityGrade]);

  /**
   * Закрытие карточки: перечитать витрину, если оценку трогали.
   *
   * <p>`onChanged` у всех трёх экранов означает «закрыть и перечитать»,
   * а `onClose` — просто закрыть. Оценка меняется, не закрывая окна,
   * поэтому колонка «Оценка состояния» за карточкой остаётся старой —
   * и обновляется ровно тогда, когда карточку закрывают.
   */
  const close = useCallback(() => {
    if (regraded) {
      onChanged();
    } else {
      onClose();
    }
  }, [regraded, onChanged, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
      if (e.key === 'ArrowRight') {
        setShown((i) => (i + 1) % Math.max(photos.length, 1));
      }
      if (e.key === 'ArrowLeft') {
        setShown((i) => (i - 1 + photos.length) % Math.max(photos.length, 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, photos.length]);

  // Оценку берём из состояния: она могла смениться на этой же карточке,
  // а `row` приезжает с витрины и о ней ещё не знает.
  const rows = [...(extraFields ?? []), ...cardFields({ ...row, qualityGrade: grade })];

  const main = photos[shown];

  /**
   * Добавляет снимки. По одному за раз, а не все разом: хранилище отвечает
   * на каждый отдельно, и параллельная отправка десяти файлов с телефона
   * через мобильную связь кончается отказом на половине.
   */
  async function addPhotos(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) {
      return;
    }
    setPhotoError('');
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadPhoto(row.id, file);
      }
      const found = await loadPhotos(row.id);
      if (mounted.current) setPhotos(found);
    } catch (e) {
      if (mounted.current) {
        setPhotoError(e instanceof ApiError ? e.message : 'Снимок не загрузился');
      }
    } finally {
      if (mounted.current) setUploading(false);
    }
  }

  async function setMain(photoId: number): Promise<void> {
    setPhotoError('');
    try {
      await makeMainPhoto(row.id, photoId);
      // Остаёмся на том же снимке: порядок в списке от смены главного
      // не меняется (он по sort_order), а прыжок на первый показывал бы
      // чужую фотографию с той же кнопкой «Сделать главным» — как будто
      // нажатие не сработало.
      const found = await loadPhotos(row.id);
      if (mounted.current) setPhotos(found);
    } catch (e) {
      if (mounted.current) {
        setPhotoError(e instanceof ApiError ? e.message : 'Не удалось назначить главным');
      }
    }
  }

  async function removePhoto(photoId: number): Promise<void> {
    setPhotoError('');
    try {
      await deletePhoto(row.id, photoId);
      const left = await loadPhotos(row.id);
      if (mounted.current) {
        setPhotos(left);
        // Удалённый мог быть последним в полосе: без сдвига экран показывал бы
        // пустое место вместо снимка.
        setShown((i) => Math.max(0, Math.min(i, left.length - 1)));
      }
    } catch (e) {
      if (mounted.current) {
        setPhotoError(e instanceof ApiError ? e.message : 'Не удалось удалить снимок');
      }
    }
  }

  /** Ячейки склада-приёмника: без адреса деталь находится только глазами. */
  async function chooseTarget(id: number | null): Promise<void> {
    setMoveTo(id);
    setMoveCell(null);
    const found = id === null ? [] : await listCells(id).catch(() => []);
    if (mounted.current) setMoveCells(found);
  }

  async function move(): Promise<void> {
    if (moveFrom === null || moveTo === null) {
      return;
    }
    setMoveError('');
    try {
      await movePart(moveFrom, moveTo, row.id, Number(moveQty), moveCell, null);
      if (mounted.current) onChanged();
    } catch (cause) {
      // 409 — «столько не лежит» или «обещано покупателю», и текст оттуда
      // называет числа.
      if (mounted.current) {
        setMoveError(cause instanceof ApiError && cause.message !== ''
          ? cause.message
          : 'Перевезти не вышло');
      }
    }
  }

  async function writeOff(): Promise<void> {
    if (writeOffAt === null) {
      return;
    }
    setWriteOffError('');
    try {
      await writeOffPart(writeOffAt, row.id, Number(writeOffQty), reason.trim());
      if (mounted.current) onChanged();
    } catch (cause) {
      // 409 — «обещано покупателю» или «столько не лежит», и текст оттуда
      // называет числа: без них человек идёт искать поломку сервера.
      if (mounted.current) {
        setWriteOffError(cause instanceof ApiError && cause.message !== ''
          ? cause.message
          : 'Списать не вышло');
      }
    }
  }

  async function addVehicle(): Promise<void> {
    if (brandId === null) {
      return;
    }
    const found = await addApplicability(row.id, brandId, modelId);
    if (!mounted.current) return;
    setFits(found);
    setModelId(null);
  }

  async function toggleEngine(matters: boolean): Promise<void> {
    const state = await setEngineMatters(row.id, matters);
    if (mounted.current) setFitState(state);
  }

  async function drop(id: number): Promise<void> {
    const left = await removeApplicability(row.id, id);
    if (mounted.current) setFits(left);
  }

  useLockedScroll();

  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="card-view" onClick={(event) => event.stopPropagation()}>
        <header className="card-view__head">
          <h2>{row.title}</h2>
          <button type="button" className="button--ghost" onClick={close}>
            Закрыть
          </button>
        </header>

        <div className="card-view__body">
          <div className="card-view__side">
            <div className="card-tabs">
              <button
                type="button"
                className={tab === 'about' ? 'card-tab card-tab--active' : 'card-tab'}
                onClick={() => setTab('about')}
              >
                Описание
              </button>
              {applicability && (
                <button
                  type="button"
                  className={tab === 'fits' ? 'card-tab card-tab--active' : 'card-tab'}
                  onClick={() => setTab('fits')}
                >
                  Применимость
                </button>
              )}
              {row.donorCode !== null && (
                <button
                  type="button"
                  className={tab === 'donor' ? 'card-tab card-tab--active' : 'card-tab'}
                  onClick={() => setTab('donor')}
                >
                  Донор
                </button>
              )}
              <button
                type="button"
                className={tab === 'history' ? 'card-tab card-tab--active' : 'card-tab'}
                onClick={() => setTab('history')}
              >
                История
              </button>
            </div>

            {tab === 'donor' ? (
              <PartDonorView donor={donor} onParts={onDonorParts} />
            ) : tab === 'fits' ? (
              <div className="card-view__fits">
                {fitState !== null && typeof fitState.engineMatters === 'boolean' && (
                  <p>
                    <strong>{fitState.engineMatters ? 'Двигатель важен' : 'Двигатель не важен'}</strong>
                    {(role === 'OWNER' || role === 'MANAGER') && (
                      <button type="button" className="mark__link"
                              onClick={() => void toggleEngine(!fitState.engineMatters)}>
                        {fitState.engineMatters ? 'сделать «не важен»' : 'сделать «важен»'}
                      </button>
                    )}
                  </p>
                )}
                {fits === null ? (
                  <p className="muted">Загружаем…</p>
                ) : fits.length > 0 ? (
                  <ul>
                    {fits.map((fit) => (
                      <li key={fit.id}>
                        {fitLabel(fit)}
                        {/* Неподтверждённую строку ставит машина — разбор наименования
                            или блока «Подходит» с Дрома (0289), — и «из наименования»
                            про вторую было бы неправдой. */}
                        {!fit.verified && <span className="muted"> · разобрано автоматически</span>}
                        {/* Кто и когда установил (0363); неизвестно — не показываем. */}
                        {fit.setBy && fit.setAt && (
                          <span className="muted">
                            {' '}· {fit.setBy}, {new Date(fit.setAt).toLocaleString('ru-RU')}
                          </span>
                        )}
                        <button
                          type="button"
                          className="mark__link"
                          onClick={() => void drop(fit.id)}
                        >
                          убрать
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : fitState?.note ? (
                  <p className="note">{fitState.note}</p>
                ) : row.brand !== null ? (
                  <p className="note">
                    Применимость не задана. Деталь находится подбором по машине
                    донора — {[row.brand, row.model].filter((v) => v).join(' ')}, — но
                    к другим машинам, к которым она тоже подходит, её не найдут.
                  </p>
                ) : (
                  <p className="note note--error">
                    Ни применимости, ни донора. По машине эту деталь не найти
                    вовсе — только поиском по названию или номеру.
                  </p>
                )}

                {/* Добавление руками: разбор из наименования берёт только
                    то, что там написано, а деталь подходит и к другим
                    машинам — знает об этом человек. */}
                {vehicles !== null && (
                  <div className="card-view__add">
                    <label className="field">
                      Марка
                      <select
                        value={brandId ?? ''}
                        onChange={(e) => {
                          setBrandId(e.target.value === '' ? null : Number(e.target.value));
                          setModelId(null);
                        }}
                      >
                        <option value="">—</option>
                        {vehicles.brands.map((brand) => (
                          <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      Модель
                      <select
                        value={modelId ?? ''}
                        onChange={(e) =>
                          setModelId(e.target.value === '' ? null : Number(e.target.value))}
                        disabled={brandId === null}
                      >
                        <option value="">любая</option>
                        {modelsOf(vehicles, brandId).map((model) => (
                          <option key={model.id} value={model.id}>{model.name}</option>
                        ))}
                      </select>
                    </label>
                    <button type="button" disabled={brandId === null} onClick={() => void addVehicle()}>
                      Добавить машину
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Отметки состояния — как в кабинете: видно сразу, привязана ли
                деталь к машине. Отрицательное состояние важнее положительного:
                деталь без донора и без применимости не находится подбором,
                и узнать об этом надо здесь, а не когда покупатель не позвонил. */}
            {applicability && (
              <div className="card-marks">
                <span className={row.donorCode === null ? 'mark mark--off' : 'mark'}>
                  {row.donorCode === null ? 'Донор не задан' : 'Донор задан'}
                  {/* Отметка без самих данных — это сообщение «данные есть,
                      но не здесь». Продавец по телефону отвечает как раз ими:
                      руль, коробка, привод машины, с которой деталь снята. */}
                  {row.donorCode !== null && (
                    <button type="button" className="mark__link"
                            onClick={() => setTab('donor')}>
                      Посмотреть
                    </button>
                  )}
                </span>
                <span className={fits !== null && fits.length > 0 ? 'mark' : 'mark mark--off'}>
                  {fits !== null && fits.length > 0
                    ? 'Применимость задана'
                    : 'Применимость не задана'}
                  <button type="button" className="mark__link" onClick={() => setTab('fits')}>
                    Посмотреть
                  </button>
                </span>
              </div>
            )}

            {/* Остаток по складам первым: продавец по телефону отвечает
                «есть, лежит на Ткацкой», а не «сейчас посмотрю». Рядом
                с ним адрес полки — до машины и номеров: кладовщик открывает
                карточку, чтобы узнать, куда идти. */}
            <PartCellBlock
              partId={row.id}
              warehouses={warehouses}
              stock={row.stock}
              reserved={row.reserved}
              role={role}
              onChanged={onChanged}
            />

            {/* Остатка нет нигде: красная плашка вместо числа «0», как в кабинете,
                к которому клиент привык (задача 0367). Цена и номер под ней как были. */}
            {Number(row.qty) === 0 && (
              <p className="stock-missing">Товар отсутствует</p>
            )}

            <div className="card-view__price">
              {row.price === null ? '—' : row.price.toLocaleString('ru-RU')}
              <span> ₽</span>
            </div>

            {(role === 'OWNER' || role === 'MANAGER') && !editing && (
              <button
                type="button"
                className="button--ghost"
                onClick={() => setEditing(true)}
              >
                Изменить
              </button>
            )}

            {editing && (
              <PartEditForm
                partId={row.id}
                row={row}
                onSaved={onChanged}
                onCancel={() => setEditing(false)}
              />
            )}

            {/* Списание здесь, а не на отдельном экране: решение принимают,
                глядя на карточку — что это за деталь, сколько её и где она
                лежит. Недостача из пересчёта обнуляет остаток, но карточку
                оставляет «в наличии»: закрыть её можно только отсюда. */}
            {/* Перевозит и кладовщик: деталь у него в руках, а перестановка
                между складами — работа, а не расход. Списывает только тот,
                кто отвечает за деньги. */}
            {!editing && ['OWNER', 'MANAGER', 'STOREKEEPER'].includes(role) && (
              moving ? (
                <div className="card-view__writeoff">
                  <label className="field">
                    Откуда
                    <select
                      value={moveFrom ?? ''}
                      onChange={(e) => setMoveFrom(
                        e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">—</option>
                      {warehouses
                        .filter((w) => Number(row.stock[String(w.id)] ?? 0) > 0)
                        .map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name} · {row.stock[String(w.id)]}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="field">
                    Куда
                    <select
                      value={moveTo ?? ''}
                      onChange={(e) => void chooseTarget(
                        e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">—</option>
                      {warehouses.filter((w) => w.id !== moveFrom).map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    Сколько
                    <input
                      inputMode="decimal"
                      value={moveQty}
                      onChange={(e) => setMoveQty(e.target.value.replace(',', '.'))}
                    />
                  </label>
                  {/* Без адреса деталь ложится на склад и находится только
                      глазами — но требовать ячейку нельзя: у клиента без
                      полок их нет вовсе. */}
                  <label className="field">
                    Ячейка на новом складе
                    <select
                      value={moveCell ?? ''}
                      onChange={(e) => setMoveCell(
                        e.target.value === '' ? null : Number(e.target.value))}
                      disabled={moveTo === null}
                    >
                      <option value="">без адреса</option>
                      {moveCells.map((cell) => (
                        <option key={cell.id} value={cell.id}>{cell.code}</option>
                      ))}
                    </select>
                  </label>
                  {moveError !== '' && <p className="note note--error">{moveError}</p>}
                  <div className="filter-row">
                    <button
                      type="button"
                      disabled={moveFrom === null || moveTo === null}
                      onClick={() => void move()}
                    >
                      Перевезти
                    </button>
                    <button
                      type="button"
                      className="button--ghost"
                      onClick={() => setMoving(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="button--ghost"
                  onClick={() => setMoving(true)}
                >
                  Перевезти
                </button>
              )
            )}

            {!editing && (role === 'OWNER' || role === 'MANAGER') && (
              writingOff ? (
                <div className="card-view__writeoff">
                  <label className="field">
                    Со склада
                    <select
                      value={writeOffAt ?? ''}
                      onChange={(e) => setWriteOffAt(
                        e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">—</option>
                      {warehouses
                        .filter((w) => Number(row.stock[String(w.id)] ?? 0) > 0)
                        .map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name} · {row.stock[String(w.id)]}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="field">
                    Сколько
                    <input
                      inputMode="decimal"
                      value={writeOffQty}
                      onChange={(e) => setWriteOffQty(e.target.value.replace(',', '.'))}
                    />
                  </label>
                  <label className="field">
                    Причина
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="разбита при разборе"
                    />
                  </label>
                  {writeOffError !== '' && (
                    <p className="note note--error">{writeOffError}</p>
                  )}
                  <div className="filter-row">
                    <button
                      type="button"
                      disabled={writeOffAt === null || reason.trim() === ''}
                      onClick={() => void writeOff()}
                    >
                      Списать
                    </button>
                    <button
                      type="button"
                      className="button--ghost"
                      onClick={() => setWritingOff(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="button--ghost"
                  onClick={() => setWritingOff(true)}
                >
                  Списать
                </button>
              )
            )}

            {/* Этикетка печатается отсюда: деталь уже открыта, искать её
                на другом экране незачем. Роли те же, что у экрана «Этикетки»
                (`LABEL_ROLES`) — второй список разошёлся бы с первым. Ключ
                по позиции: открыв следующую карточку, человек обязан увидеть
                её наклейку, а не оставшуюся от предыдущей. */}
            {!editing && LABEL_ROLES.includes(role) && (
              <PartLabelPrint
                key={row.id}
                code={row.code}
                title={row.title}
                price={row.price}
                condition={row.condition}
              />
            )}

            {!editing && row.description !== null && row.description !== '' && (
              <p className="card-view__note">{row.description}</p>
            )}

            {!editing && (
              <dl className="card-view__fields">
                {rows.map(([title, value]) => (
                  <div key={title}>
                    <dt>{title}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            </>
            )}
          </div>

          {/* История занимает место снимка, а не втискивается в столбец
              сведений: правка наименования — это две строки текста длиной
              в заголовок, и в колонке шириной в треть экрана от неё остаются
              переносы по одному слову. Снимок в это время и не нужен:
              историю открывают, когда разбираются, а не когда смотрят
              на деталь. Поймано живым прогоном. */}
          {tab === 'history' ? (
            <div className="card-view__history">
              <PartHistoryView history={history} tab={historyTab} onTab={setHistoryTab} />
            </div>
          ) : (
          <div className="card-view__photos">
            {/* Над снимком, как в системе, из которой приходят клиенты:
                оценка — это то, что покупатель читает сразу после цены
                и фотографии, и ставят её, глядя на саму деталь. */}
            {quality && (
              <PartQualityBlock
                partId={row.id}
                grade={grade}
                role={role}
                onGraded={(next) => { setGrade(next); setRegraded(true); }}
              />
            )}

            {main !== undefined && (
              <>
                <div className="card-view__frame">
                  {photos.length > 1 && (
                    <button
                      type="button"
                      className="card-view__arrow card-view__arrow--prev"
                      onClick={() => setShown((i) => (i - 1 + photos.length) % photos.length)}
                    >
                      ‹
                    </button>
                  )}
                  <img className="card-view__main" src={main.url} alt="" />
                  {photos.length > 1 && (
                    <button
                      type="button"
                      className="card-view__arrow card-view__arrow--next"
                      onClick={() => setShown((i) => (i + 1) % photos.length)}
                    >
                      ›
                    </button>
                  )}
                </div>
                {photos.length > 1 && (
                  <div className="card-view__strip">
                    {photos.map((photo, i) => (
                      <button
                        key={photo.photoId}
                        type="button"
                        className={i === shown ? 'thumb-button is-shown' : 'thumb-button'}
                        onClick={() => setShown(i)}
                      >
                        <img className="thumb" src={photo.url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Скачивание — всем, кто открыл карточку: это ровно те снимки,
                которые он и так видит. Пока их не было пачкой, продавец
                девять раз открывал снимок стрелкой и девять раз сохранял
                его правой кнопкой — минута механической работы на глазах
                у ждущего на линии покупателя. */}
            {!uploading && (
              <div className="card-view__download">
                {photos.length > 0 ? (
                  <a className="button--ghost" href={photoArchiveUrl(row.id)} download>
                    Скачать все фото
                  </a>
                ) : (
                  <>
                    <button type="button" className="button--ghost" disabled>
                      Скачать все фото
                    </button>
                    {/* Погашенная кнопка называет причину — как «Печать
                        штрих-кода» у позиции без номера: серая кнопка
                        без объяснения читается как поломка. */}
                    <p className="note">Снимков нет — скачивать нечего</p>
                  </>
                )}
              </div>
            )}

            {/* Досъёмка: снимок делают телефоном при приёмке, но деталь,
                приехавшую переносом без картинок, доснять было негде вовсе —
                а на разборке продаёт фотография. */}
            {(role === 'OWNER' || role === 'MANAGER' || role === 'SELLER') && (
              <div className="card-view__upload">
                {main !== undefined && (
                  <div className="filter-row">
                    {!main.main && (
                      <button
                        type="button"
                        className="button--ghost"
                        onClick={() => void setMain(main.photoId)}
                      >
                        Сделать главным
                      </button>
                    )}
                    <button
                      type="button"
                      className="button--ghost"
                      onClick={() => void removePhoto(main.photoId)}
                    >
                      Удалить снимок
                    </button>
                  </div>
                )}
                <label className="button--ghost card-view__add-photo">
                  {uploading ? 'Загружаем…' : 'Добавить фото'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploading}
                    onChange={(e) => void addPhotos(e.currentTarget.files)}
                  />
                </label>
                {photoError !== '' && <p className="note note--error">{photoError}</p>}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

